import { normalizeDateFromAPI } from "../utils/applicationFormatters";
import { formatDateForAPI } from "@/features/applicant/utils/addressHelpers";
import type {
  ApplicationDetailData,
  ApplicationLayerData,
} from "../types/application";
import type { PaymentAdjustmentHistoryLine } from "../types/modals";
import type {
  ResidentApplicationAddresses,
  ResidentApplicationEmploymentContext,
} from "../types/services";
import type { AboutYourselfPersonalField, AddressWithRaId } from "../types/pages";
import { LEASE_DETAILS_EMAIL_PROGRESS_STATUSES } from '../constants';

export {
  CO_RESIDENT_RELATIONSHIP_TYPES as RELATIONSHIP_TYPES,
  LEASE_DETAILS_EMAIL_PROGRESS_STATUSES,
  STATUS_MAP,
} from '../constants';

export const isPresent = (value: unknown): boolean => {
  if (value === 0 || value === "0" || value === "0.00") return true;
  if (value === undefined || value === null) return false;
  return String(value).trim() !== "";
};

export const normalizeRentForCompare = (value: unknown): string => {
  if (!isPresent(value)) return "";
  const numeric = parseFloat(String(value).replace(/[^0-9.-]/g, ""));
  return Number.isNaN(numeric) ? String(value ?? "").trim() : numeric.toFixed(2);
};

export const normalizeRentForAngularPayload = (value: unknown): string => {
  const raw = String(value ?? "").replace(/[^0-9.-]/g, "");
  if (!raw) return "";
  return raw.replace(/\.00$/, "");
};

export const normalizePhoneForAngularPayload = (value: unknown): string =>
  String(value ?? "").replace(/\D/g, "");

/** Coerce numeric API ids (fk_property_id, fk_agent_id) to numbers like Angular payloads. */
export const coerceApiNumericId = (
  value: unknown,
): number | undefined => {
  if (value === undefined || value === null || String(value).trim() === "") {
    return undefined;
  }
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : undefined;
};

export const orderPropertyPayloadLikeAngular = (payload: Record<string, unknown>) => {
  const ordered: Record<string, unknown> = {};
  const keys = [
    "applied_property",
    "fk_property_id",
    "fk_temp_property_id",
    "other_agent",
    "fk_agent_id",
    "rent_amount",
    "lease_start_date",
    "lease_duration",
    "invite_code",
    "rent_lease_reason",
    "other_individuals",
    "co_resident",
    "unit_number",
    "dont_update_lease_date",
    "resend_lease_details_email",
    "enter_date",
    "validation_scope",
    "validation_tab",
    "from_admin",
  ];
  for (const key of keys) {
    if (Object.prototype.hasOwnProperty.call(payload, key)) {
      ordered[key] = payload[key];
    }
  }
  for (const [key, value] of Object.entries(payload)) {
    if (!Object.prototype.hasOwnProperty.call(ordered, key)) {
      ordered[key] = value;
    }
  }
  return ordered;
};

export const normalizeLeaseDateForCompare = (value: unknown): string => {
  if (!value) return "";
  const raw = String(value).trim();
  return normalizeDateFromAPI(raw) || formatDateForAPI(raw);
};

export const isBeforeToday = (value: unknown): boolean => {
  const raw = value == null ? "" : String(value).trim();
  const normalized =
    /^\d{1,2}\/\d{1,2}\/\d{4}$/.test(raw)
      ? formatDateForAPI(raw)
      : normalizeLeaseDateForCompare(raw);
  if (!normalized) return false;
  const [year, month, day] = normalized.split("-").map(Number);
  const date = new Date(year, month - 1, day);
  if (Number.isNaN(date.getTime())) return false;
  date.setHours(0, 0, 0, 0);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return date < today;
};

/** Angular parity: `moment(leaseDate).isBefore(moment())` for date-picker values (start of local day). */
export const isLeaseDateBeforeNow = (value: unknown): boolean => {
  const normalized = normalizeLeaseDateForCompare(value);
  if (!normalized) return false;
  const [year, month, day] = normalized.split("-").map(Number);
  const leaseDate = new Date(year, month - 1, day);
  if (Number.isNaN(leaseDate.getTime())) return false;
  return leaseDate.getTime() < Date.now();
};

export const isApiOne = (value: unknown): boolean => String(value ?? "") === "1";

export const nullToUndefined = <T,>(value: T | null | undefined): T | undefined =>
  value === null ? undefined : value;

/** Partial parent delta: empty incoming values must not wipe stored `user_data` (Angular parity). */
export function resolveAboutYourselfPersonalField(
  formData: Record<string, unknown>,
  applicationData: ApplicationLayerData | null | undefined,
  field: AboutYourselfPersonalField,
): string | undefined {
  const userData = (applicationData?.user_data ?? {}) as Record<string, unknown>;
  const stored = userData[field] ?? applicationData?.[field as keyof ApplicationLayerData];
  const incoming = formData[field];
  if (incoming === undefined || incoming === null) {
    return nullToUndefined(stored as string | null | undefined);
  }
  const incomingStr = String(incoming).trim();
  const storedStr = stored == null ? "" : String(stored).trim();
  if (!incomingStr && storedStr) {
    return nullToUndefined(stored as string | null | undefined);
  }
  return nullToUndefined(incoming as string | null | undefined);
}

export const getUnknownErrorMessage = (err: unknown, fallback: string): string => {
  if (err instanceof Error && err.message) return err.message;
  if (typeof err === "object" && err !== null) {
    if (
      "message" in err &&
      typeof (err as { message?: unknown }).message === "string"
    ) {
      return (err as { message: string }).message;
    }
    if (
      "error" in err &&
      typeof (err as { error?: unknown }).error === "string"
    ) {
      return (err as { error: string }).error;
    }
  }
  return fallback;
};

export const normalizeStatusForCompare = (value: unknown): string => {
  return String(value ?? "")
    .trim()
    .replace(/([a-z])([A-Z])/g, "$1_$2")
    .replace(/[^a-zA-Z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "")
    .toUpperCase();
};

export const normalizeTextForCompare = (value: unknown): string => {
  if (value === undefined || value === null) return "";
  return String(value);
};

export const isApprovedWithLeaseEmailStatus = (
  application: ApplicationLayerData | ApplicationDetailData | null | undefined,
): boolean => {
  const applicationStatus = normalizeStatusForCompare(
    application?.application_status ?? application?.applicant_status,
  );
  const progressStatus = normalizeStatusForCompare(application?.progress_status);
  return (
    applicationStatus === "APPROVED" &&
    Array.from(LEASE_DETAILS_EMAIL_PROGRESS_STATUSES).some(
      (status) => normalizeStatusForCompare(status) === progressStatus,
    )
  );
};

export const isNonActiveRentalStatus = (status: unknown): boolean => {
  const normalized = String(status ?? "").trim().toUpperCase();
  return normalized !== "" && normalized !== "ACTIVE" && normalized !== "NOT STARTED";
};

export const hasReimbursementRentalApplicationStatus = (
  application: ApplicationLayerData | null | undefined,
  applicationAllData: ApplicationDetailData | null | undefined,
): boolean => {
  return (
    isNonActiveRentalStatus(application?.rental_status) ||
    isNonActiveRentalStatus(applicationAllData?.rental_status)
  );
};

/** Legacy PHP path expects string flags in `address_data` (see applicant `addressHelpers.buildAddressData`). */
export function toApiCurrentlyLivingFlag(value: unknown): "1" | "0" {
  if (value === true || value === "1" || value === 1) return "1";
  return "0";
}

export function trimStreetField(v: unknown): string {
  if (v === undefined || v === null) return "";
  return String(v).trim();
}

/** Prefer baseline `street_type` when form omits it — US backends reject empty values. */
export function resolveStreetTypeForApi(
  addr: Record<string, unknown> | null | undefined,
  baseline: Record<string, unknown> | null | undefined,
): string {
  const fromForm = trimStreetField(addr?.street_type);
  if (fromForm) return fromForm;
  const fromBaseline = trimStreetField(baseline?.street_type);
  if (fromBaseline) return fromBaseline;
  const country = trimStreetField(addr?.country).toUpperCase();
  const us = country === "US" || country === "USA" || country === "";
  return us ? "ST" : "";
}

/** Coerce empty form `ra_id` to baseline so updates never POST `ra_id: ""` (Angular parity). */
export function resolveAddressRaId(
  fromForm: unknown,
  fromBaseline: unknown,
): number | string | "" {
  const empty = (v: unknown) => v === undefined || v === null || v === "";
  if (!empty(fromForm)) return fromForm as number | string;
  if (!empty(fromBaseline)) return fromBaseline as number | string;
  return "";
}

/** Angular alternate_address shape only — preserve baseline `street_type` (often null). */
export function buildAlternateAddressForApi(
  addr: Record<string, unknown> | null | undefined,
  baseline: Record<string, unknown> | null | undefined,
): Record<string, unknown> {
  const a = (addr ?? {}) as Record<string, unknown>;
  const fromFormStreetType = trimStreetField(a.street_type);
  const fromBaselineStreetType = baseline?.street_type as unknown;
  return {
    ra_id: resolveAddressRaId(a.ra_id, baseline?.ra_id),
    street_address: a.street_address ?? "",
    outside_us: (a.outside_us as boolean | undefined) ?? false,
    house_no: a.house_no ?? "",
    street: a.street ?? "",
    street_type: fromFormStreetType
      ? fromFormStreetType
      : fromBaselineStreetType !== undefined
        ? fromBaselineStreetType
        : null,
    city: a.city ?? "",
    country: a.country ?? "US",
    state: a.state ?? "",
    zip_code: a.zip_code ?? "",
  };
}

function pickDeletedIds(deleted: unknown, key: string): Array<number | string> {
  if (!deleted || typeof deleted !== "object") return [];
  const list = (deleted as Record<string, unknown>)[key];
  if (!Array.isArray(list)) return [];
  return list.filter(
    (id): id is number | string =>
      (typeof id === "number" || typeof id === "string") &&
      String(id).trim() !== "",
  );
}

/** Extract deleted address ra_ids for Angular `deleted_data` parity. */
export function extractDeletedAddressIds(deleted: unknown): {
  previous_address: Array<number | string>;
  alternate_address: Array<number | string>;
} {
  return {
    previous_address: pickDeletedIds(deleted, "previous_address"),
    alternate_address: pickDeletedIds(deleted, "alternate_address"),
  };
}

/** Angular `deleted_data.resident_job` — job_ids removed during this edit session. */
export function extractDeletedJobIds(deleted: unknown): Array<number | string> {
  return pickDeletedIds(deleted, "resident_job");
}

/** Angular `enteredDate($event, section, index)` key format for employment date fields. */
export function employmentEnterDateKey(
  field: "job_start_date" | "job_end_date",
  section: "current_employment" | "previous_employment" | "other_source",
  index: number,
): string {
  return `${field}_${section}_${index}`;
}

const isEmptyRaId = (value: unknown): boolean =>
  value === undefined || value === null || value === "";

/** Copy a non-empty source `ra_id` onto a target address only when the target lacks one. */
function fillRaId<T extends AddressWithRaId>(
  target: T | null | undefined,
  sourceRaId: unknown,
): T | null | undefined {
  if (!target || !isEmptyRaId(target.ra_id) || isEmptyRaId(sourceRaId)) {
    return target;
  }
  return { ...target, ra_id: sourceRaId as number | string };
}

function fillRaIdByIndex<T extends AddressWithRaId>(
  target: T[] | null | undefined,
  source: ReadonlyArray<AddressWithRaId> | null | undefined,
): T[] | null | undefined {
  if (!Array.isArray(target) || !Array.isArray(source)) return target;
  return target.map((addr, index) => fillRaId(addr, source[index]?.ra_id) ?? addr);
}

/** Merge address ra_id from resident_application so saves UPDATE instead of INSERT (Angular parity). */
export function mergeAddressRaIds(
  layer: ApplicationLayerData,
  addresses: ResidentApplicationAddresses | null | undefined,
): ApplicationLayerData {
  if (!layer || !addresses) return layer;
  return {
    ...layer,
    oldapplication: addresses.oldapplication ?? layer.oldapplication,
    current_address: fillRaId(
      layer.current_address as AddressWithRaId | undefined,
      (addresses.current_address as AddressWithRaId | null | undefined)?.ra_id,
    ),
    previous_address: fillRaIdByIndex(
      layer.previous_address as AddressWithRaId[] | undefined,
      addresses.previous_address as ReadonlyArray<AddressWithRaId> | undefined,
    ),
    alternate_address: fillRaIdByIndex(
      layer.alternate_address as AddressWithRaId[] | undefined,
      addresses.alternate_address as ReadonlyArray<AddressWithRaId> | undefined,
    ),
  } as ApplicationLayerData;
}

/** Merge employment context for import prompts without clobbering unsaved imported jobs. */
export function mergeEmploymentResidentContext(
  layer: ApplicationLayerData,
  context: ResidentApplicationEmploymentContext | null | undefined,
): ApplicationLayerData {
  if (!layer || !context) return layer;
  const next: ApplicationLayerData = { ...layer };
  if (context.oldapplication) {
    next.oldapplication = context.oldapplication;
  }
  const layerGap = layer.job_gap_description;
  const contextGap = context.job_gap_description;
  if (
    contextGap !== undefined &&
    contextGap !== null &&
    String(contextGap).trim() !== "" &&
    (layerGap === undefined || layerGap === null || String(layerGap).trim() === "")
  ) {
    next.job_gap_description = contextGap;
  }
  return next;
}

export function hasContractDataForRisk(
  applicationAllData: ApplicationDetailData | null,
): boolean {
  const hist = applicationAllData?.payment_history as unknown[] | undefined;
  if (!Array.isArray(hist)) return false;
  return hist.some((p: unknown) => {
    const row = p as { payment_type?: string; transaction_status?: string };
    return (
      ["CONTRACT", "CONTRACT_INVOICE"].includes(row.payment_type || "") &&
      row.transaction_status !== "Refunded"
    );
  });
}

export function findFirstContractPayment(
  applicationAllData: ApplicationDetailData | null,
): PaymentAdjustmentHistoryLine | null {
  const hist = applicationAllData?.payment_history;
  if (!Array.isArray(hist)) return null;
  const match = hist.find(
    (payment) =>
      ["CONTRACT", "CONTRACT_INVOICE"].includes(payment.payment_type ?? "") &&
      payment.transaction_status !== "Refunded",
  );
  return match ? (match as PaymentAdjustmentHistoryLine) : null;
}

/** Angular: strip leading zeros from route applicant id (`${+params.applicant_id}`). */
export function normalizeApplicantId(
  applicantId: string | number | null | undefined,
): string {
  const raw = String(applicantId ?? "").trim();
  if (!/^\d+$/.test(raw)) return raw;
  return raw.replace(/^0+/, "") || "0";
}

export function resolveApplicationLayerFromDetail(
  data: ApplicationDetailData,
  applicantId: string,
): { applicationData: ApplicationLayerData; isCoresident: boolean } {
  if (data.co_resident_application) {
    const coresidents = Object.values(data.co_resident_application);
    const normalizedId = normalizeApplicantId(applicantId);
    const currentApplicant = coresidents.find(
      (app) => normalizeApplicantId(app.applicant_id) === normalizedId,
    );
    if (currentApplicant) {
      return { applicationData: currentApplicant, isCoresident: coresidents.length > 1 };
    }
    const primary = coresidents.find((app) => app.is_primary);
    if (primary) {
      return { applicationData: primary, isCoresident: coresidents.length > 1 };
    }
  }
  return { applicationData: data, isCoresident: false };
}
